/** Llamadas HTTP del feature Arranques + Medidores + Instalaciones. */
import { apiRequest } from "../../lib/api";

// El corte por morosidad es estado del Contrato, no del Arranque. El arranque
// sólo lleva estados físicos (decisión 2026-07-10, ítem 2.6 del plan).
export type EstadoArranque = "Activo" | "Retirado" | "Inactivo";
export type EstadoMedidor = "Bueno" | "Malo" | "En Reparación" | "De Baja";
export type GeorefOrigen = "manual_admin" | "gps_lectura" | "corregido";

// ────────────────────────────────────────────────────────────────────────
// Arranques
// ────────────────────────────────────────────────────────────────────────

export interface ArranqueRead {
  id: number;
  empresa_id: number;
  sector_id: number | null;
  ruta_id: number | null;
  direccion: string;
  numero_casa: string | null;
  referencia: string | null;
  latitud: number | null;
  longitud: number | null;
  georef_origen: GeorefOrigen | null;
  georef_actualizado_at: string | null;
  estado: EstadoArranque;
  codigo_arranque: string | null;
  orden_ruta: number | null;
  /** URL presignada (1h) o null si el arranque no tiene foto de referencia. */
  foto_referencia_url: string | null;
  foto_referencia_actualizado_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ArranqueCreatePayload {
  direccion: string;
  sector_id?: number | null;
  numero_casa?: string | null;
  referencia?: string | null;
  latitud?: number | null;
  longitud?: number | null;
  estado?: EstadoArranque;
  codigo_arranque?: string | null;
  orden_ruta?: number | null;
}

export interface ArranqueUpdatePayload {
  direccion?: string;
  sector_id?: number | null;
  numero_casa?: string | null;
  referencia?: string | null;
  latitud?: number | null;
  longitud?: number | null;
  estado?: EstadoArranque;
  codigo_arranque?: string | null;
  orden_ruta?: number | null;
}

export function listarArranques(sectorId?: number): Promise<ArranqueRead[]> {
  const qs = sectorId ? `?sector_id=${sectorId}` : "";
  return apiRequest<ArranqueRead[]>(`/api/v1/arranques${qs}`);
}

export function crearArranque(p: ArranqueCreatePayload): Promise<ArranqueRead> {
  return apiRequest<ArranqueRead>("/api/v1/arranques", {
    method: "POST",
    body: p,
  });
}

export function actualizarArranque(
  id: number,
  p: ArranqueUpdatePayload,
): Promise<ArranqueRead> {
  return apiRequest<ArranqueRead>(`/api/v1/arranques/${id}`, {
    method: "PATCH",
    body: p,
  });
}

export function eliminarArranque(id: number): Promise<void> {
  return apiRequest<void>(`/api/v1/arranques/${id}`, { method: "DELETE" });
}

export function asociarFotoReferencia(
  id: number,
  foto_storage_key: string,
): Promise<ArranqueRead> {
  return apiRequest<ArranqueRead>(`/api/v1/arranques/${id}/foto-referencia`, {
    method: "PATCH",
    body: { foto_storage_key },
  });
}

export function eliminarFotoReferencia(id: number): Promise<ArranqueRead> {
  return apiRequest<ArranqueRead>(`/api/v1/arranques/${id}/foto-referencia`, {
    method: "DELETE",
  });
}

export interface ActualizarCoordsPayload {
  latitud: number;
  longitud: number;
}

export function actualizarCoordsArranque(
  id: number,
  payload: ActualizarCoordsPayload,
): Promise<ArranqueRead> {
  return apiRequest<ArranqueRead>(`/api/v1/arranques/${id}/coords`, {
    method: "POST",
    body: payload,
  });
}

// ────────────────────────────────────────────────────────────────────────
// Medidores
// ────────────────────────────────────────────────────────────────────────

export interface MedidorRead {
  id: number;
  empresa_id: number;
  numero_serie: string;
  marca: string | null;
  diametro: string;
  tipo: string;
  subtipo: string | null;
  tecnologia: string | null;
  arranque_id: number | null;
  estado: EstadoMedidor;
  fecha_instalacion: string | null;
  lectura_inicial: number;
  created_at: string;
  updated_at: string;
}

export interface MedidorCreatePayload {
  numero_serie: string;
  marca?: string | null;
  diametro?: string;
  tipo?: string;
  subtipo?: string | null;
  tecnologia?: string | null;
  lectura_inicial?: number;
  estado?: EstadoMedidor;
}

export interface MedidorUpdatePayload {
  marca?: string | null;
  diametro?: string;
  tipo?: string;
  subtipo?: string | null;
  tecnologia?: string | null;
  estado?: EstadoMedidor;
}

export function listarMedidores(
  enBodega?: boolean,
): Promise<MedidorRead[]> {
  const qs =
    enBodega === undefined ? "" : `?en_bodega=${enBodega ? "true" : "false"}`;
  return apiRequest<MedidorRead[]>(`/api/v1/medidores${qs}`);
}

export function crearMedidor(p: MedidorCreatePayload): Promise<MedidorRead> {
  return apiRequest<MedidorRead>("/api/v1/medidores", {
    method: "POST",
    body: p,
  });
}

export function actualizarMedidor(
  id: number,
  p: MedidorUpdatePayload,
): Promise<MedidorRead> {
  return apiRequest<MedidorRead>(`/api/v1/medidores/${id}`, {
    method: "PATCH",
    body: p,
  });
}

// ────────────────────────────────────────────────────────────────────────
// Instalaciones
// ────────────────────────────────────────────────────────────────────────

export interface InstalacionRead {
  id: number;
  empresa_id: number;
  arranque_id: number;
  medidor_id: number;
  fecha_instalacion: string;
  lectura_al_instalar: number;
  fecha_retiro: string | null;
  lectura_al_retirar: number | null;
  motivo_cambio: string | null;
  tecnico_id: number | null;
  observaciones: string | null;
  created_at: string;
  updated_at: string;
  /** Aviso no bloqueante del retiro (lectura del período sin facturar). */
  advertencia?: string | null;
}

export interface InstalarPayload {
  arranque_id: number;
  medidor_id: number;
  fecha_instalacion: string;
  lectura_al_instalar?: number;
  tecnico_id?: number | null;
  observaciones?: string | null;
}

export interface RetirarPayload {
  fecha_retiro: string;
  lectura_al_retirar: number;
  motivo_cambio?: string | null;
}

export function instalarMedidor(p: InstalarPayload): Promise<InstalacionRead> {
  return apiRequest<InstalacionRead>("/api/v1/medidores/instalaciones", {
    method: "POST",
    body: p,
  });
}

export function retirarMedidor(
  arranqueId: number,
  p: RetirarPayload,
): Promise<InstalacionRead> {
  return apiRequest<InstalacionRead>(
    `/api/v1/medidores/instalaciones/retirar/${arranqueId}`,
    { method: "POST", body: p },
  );
}

export function historialArranque(
  arranqueId: number,
): Promise<InstalacionRead[]> {
  return apiRequest<InstalacionRead[]>(
    `/api/v1/medidores/arranque/${arranqueId}/historial`,
  );
}
